import { DIATONIC_CHORDS } from "../data/diatonicChords";
import { BORROWED_CHORDS } from "../data/BorrowedChord";
import { TRIAD_CHORDS } from "../data/TriadChords";
import { RELATIVE_CHORDS } from "../data/RelativeChords";
import { parseChordSymbol } from "./chordParse";

const NOTE_TO_PITCH = {
  C: 0, "B#": 0,
  "C#": 1, Db: 1,
  D: 2,
  "D#": 3, Eb: 3,
  E: 4, Fb: 4,
  F: 5, "E#": 5,
  "F#": 6, Gb: 6,
  G: 7,
  "G#": 8, Ab: 8,
  A: 9,
  "A#": 10, Bb: 10,
  B: 11, Cb: 11,
};

function extractChordRoot(chord) {
  return chord?.match(/^[A-G](?:#|b)?/)?.[0] || null;
}

function tonicOfKey(key) {
  return (key || "").replace(/m$/, "");
}

function pitchOf(note) {
  return NOTE_TO_PITCH[note] ?? null;
}

function isDominantFamilyChord(chord) {
  const root = extractChordRoot(chord);
  if (!root) return false;
  const rest = chord.slice(root.length);
  // Dominant-family: starts with 7 (e.g. 7, 7(b9), 7sus4)
  return rest.startsWith("7");
}

function isBackdoorDominant(chord, key) {
  if (!isDominantFamilyChord(chord)) return false;
  const tonic = tonicOfKey(key);
  const tonicPitch = pitchOf(tonic);
  const rootPitch = pitchOf(extractChordRoot(chord));
  if (tonicPitch == null || rootPitch == null) return false;
  // bVII7 -> I : root is -2 semitones from tonic (mod 12)
  return rootPitch === (tonicPitch + 10) % 12;
}

function isSubstituteDominant(chord, key) {
  if (!isDominantFamilyChord(chord)) return false;
  const tonic = tonicOfKey(key);
  const tonicPitch = pitchOf(tonic);
  const rootPitch = pitchOf(extractChordRoot(chord));
  if (tonicPitch == null || rootPitch == null) return false;
  // bII7 (tritone substitution for V7) : root is +1 semitone from tonic
  return rootPitch === (tonicPitch + 1) % 12;
}

function isTriadChord(chord, key) {
  if (!chord || !key) return false;
  const triadsForKey = TRIAD_CHORDS[key] || {};
  return Object.values(triadsForKey).includes(chord);
}

function isBlackadderChord(chord, key) {
  const parsed = parseChordSymbol(chord);
  if (!parsed || !parsed.bass) return false;
  const isAugmented = parsed.quality === "aug" || parsed.quality === "aug7";
  if (!isAugmented) return false;

  const tonic = tonicOfKey(key);
  const tonicPitch = pitchOf(tonic);
  const rootPitch = pitchOf(parsed.root);
  const bassPitch = pitchOf(parsed.bass);
  if (tonicPitch == null || rootPitch == null || bassPitch == null) return false;

  // Blackadder Code (major): Vaug/bII
  return rootPitch === (tonicPitch + 7) % 12 && bassPitch === (tonicPitch + 1) % 12;
}

export function analyzeChordBaseType(chord, key) {
  if (!chord || chord.trim() === "") {
    return null;
  }

  const diatonicSet = new Set(Object.values(DIATONIC_CHORDS[key] || {}));
  if (diatonicSet.has(chord)) {
    return "Diatonic";
  }

  if (isBlackadderChord(chord, key)) {
    return "Blackadder Code";
  }

  if (isBackdoorDominant(chord, key)) {
    return "Backdoor Dominant";
  }

  if (isSubstituteDominant(chord, key)) {
    return "Substitute Dominant";
  }

  const secDomRegex = /^[A-G][b#]?7(?:\([^)]*\))?$/;
  if (secDomRegex.test(chord)) {
    const rootMatch = chord.match(/^[A-G][b#]?/);
    if (rootMatch) {
      const root = rootMatch[0];
      for (const targetDegree in DIATONIC_CHORDS[key]) {
        const targetChord = DIATONIC_CHORDS[key][targetDegree];
        if (targetChord.startsWith(root)) {
          return "Secondary Dominant";
        }
      }
    }
  }

  const borrowedChords = BORROWED_CHORDS[key] || [];
  if (borrowedChords.includes(chord)) {
    return "Parallel";
  }

  const relativeChords = new Set();
  const collectRelativeChords = (targetKey) => {
    const entry = RELATIVE_CHORDS[targetKey];
    if (entry) {
      Object.values(entry).forEach((value) => relativeChords.add(value));
    }
  };
  collectRelativeChords(key);
  const relativeKeyLabel = key.endsWith("m")
    ? key.replace(/m$/, "")
    : `${key}m`;
  if (relativeKeyLabel !== key) {
    collectRelativeChords(relativeKeyLabel);
  }

  if (relativeChords.has(chord)) {
    return "Parallel";
  }

  return "Non-diatonic";
}

export function analyzeChordType(chord, key) {
  const baseType = analyzeChordBaseType(chord, key);
  const labels = [];
  if (baseType) labels.push(baseType);
  // Only add Triad label when it's a triad (no 7th). Do NOT add a Seventh label.
  if (isTriadChord(chord, key)) {
    labels.push("Triad");
  }
  return labels;
}

export function analyzeChordLabels(chord, key) {
  const baseType = analyzeChordBaseType(chord, key);
  const labels = [];
  if (baseType) labels.push(baseType);
  // Only add Triad label for triads.
  if (isTriadChord(chord, key)) {
    labels.push("Triad");
  }
  return labels;
}
